import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, MapPin, Navigation, X, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAP_ID = "e2229d09b8bbc0139f850641";

// Parse address components helper
const parseAddressComponents = (place, lat, lng) => {
  const components = {
    building_no: "",
    street: "",
    neighborhood: "",
    district: "",
    city: "",
    postal_code: "",
  };

  (place.address_components || []).forEach((component) => {
    const types = component.types;
    if (types.includes("street_number")) {
      components.building_no = component.long_name;
    }
    if (types.includes("route")) {
      components.street = component.long_name;
    }
    // Mahalle için birden fazla type kontrol et
    if (types.includes("neighborhood") || 
        types.includes("sublocality_level_1") ||
        types.includes("sublocality_level_2") ||
        types.includes("sublocality")) {
      // İlk bulduğunu al (daha spesifik olan öncelikli)
      if (!components.neighborhood) {
        components.neighborhood = component.long_name;
      }
    }
    if (types.includes("administrative_area_level_2")) {
      components.district = component.long_name;
    }
    // İlçe bazen administrative_area_level_4'te de olabiliyor
    if (types.includes("administrative_area_level_4") && !components.district) {
      components.district = component.long_name;
    }
    if (types.includes("administrative_area_level_1")) {
      components.city = component.long_name;
    }
    if (types.includes("postal_code")) {
      components.postal_code = component.long_name;
    }
  });

  // Eğer mahalle hala boşsa, place.name'den çıkarmayı dene
  // Google bazen mahalle adını ana isim olarak döndürüyor
  if (!components.neighborhood && place.name) {
    // Eğer place.name cadde değilse ve "Mah" veya mahalle gibi bir şey içermiyorsa
    // ama formatted_address'in başında farklı bir isim varsa, onu mahalle olarak al
    const formatted = place.formatted_address || "";
    const parts = formatted.split(",").map(p => p.trim());
    
    // İlk parça genellikle en spesifik lokasyon (mahalle veya sokak)
    if (parts.length > 0) {
      const firstPart = parts[0];
      // Eğer cadde/sokak zaten varsa ve ilk parça farklıysa, mahalle olabilir
      if (components.street && !firstPart.includes(components.street)) {
        // Bina numarası içermiyorsa mahalle olarak kabul et
        if (!/No[:\s]*\d|^\d+$/.test(firstPart)) {
          components.neighborhood = firstPart;
        }
      }
      // Eğer cadde yoksa ve ilk parça "Cd", "Sk", "Blv" içermiyorsa
      if (!components.street && !components.neighborhood) {
        if (!/(Cd\.|Cad\.|Sk\.|Sok\.|Blv\.|Bulv\.)/i.test(firstPart)) {
          components.neighborhood = firstPart;
        }
      }
    }
  }

  return components;
};

// Google Maps Integrated Location Picker
export default function GoogleMapLocationPicker({
  onLocationSelect,
  initialLocation = { lat: 41.0082, lng: 28.9784 },
  initialAddress = "",
  height = "400px",
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const inputRef = useRef(null);
  const searchFunctionRef = useRef(null);
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);

  
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState(initialAddress);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [addressDetails, setAddressDetails] = useState(null);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Reverse geocode function
  const doReverseGeocode = async (lat, lng) => {
    if (!window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    
    try {
      const response = await geocoder.geocode({ location: { lat, lng } });
      
      if (response.results && response.results[0]) {
        const place = response.results[0];
        const details = parseAddressComponents(place, lat, lng);
        details.formatted_address = place.formatted_address;
        details.lat = lat;
        details.lng = lng;
        details.place_id = place.place_id;

        setSelectedLocation({ lat, lng });
        setAddressDetails(details);
        setSearchValue(place.formatted_address || "");
        setError(null);

        onLocationSelect?.(details);
      }
    } catch (err) {
      console.error("Geocoding error:", err);
    }
  };

  // Place marker and get address
  const placeMarker = (lat, lng) => {
    if (!mapInstanceRef.current || !markerRef.current) return;

    markerRef.current.position = { lat, lng };
    markerRef.current.map = mapInstanceRef.current;
    mapInstanceRef.current.panTo({ lat, lng });

    doReverseGeocode(lat, lng);
  };

  // Load Google Maps and initialize
  useEffect(() => {
    let isMounted = true;

    const initMap = () => {
      if (!mapRef.current || !window.google?.maps) return;

      try {
        // Create map
        const map = new window.google.maps.Map(mapRef.current, {
          center: initialLocation,
          zoom: 13,
          mapId: GOOGLE_MAP_ID,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });

        mapInstanceRef.current = map;

        // Create marker (AdvancedMarkerElement)
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          map: map,
          gmpDraggable: true,
        });

        markerRef.current = marker;

        // Click on map
        map.addListener("click", (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          marker.position = { lat, lng };
          marker.map = map;
          map.panTo({ lat, lng });
          doReverseGeocode(lat, lng);
        });

        // Drag marker
        marker.addEventListener("gmp-dragend", () => {
          const pos = marker.position;
          const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
          const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
          doReverseGeocode(lat, lng);
        });

        // Setup autocomplete (try new API first, fallback to legacy)
        if (inputRef.current) {
          try {
            const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
              componentRestrictions: { country: "tr" },
              fields: ["formatted_address", "geometry", "address_components", "name", "place_id"],
              types: ["geocode", "establishment"],
            });

            autocomplete.bindTo("bounds", map);

            autocomplete.addListener("place_changed", () => {
              const place = autocomplete.getPlace();

              if (!place.geometry?.location) {
                setError(t("common:noLocationFound"));
                return;
              }

              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();

              map.setCenter({ lat, lng });
              map.setZoom(17);
              marker.position = { lat, lng };
              marker.map = map;

              const details = parseAddressComponents(place, lat, lng);
              const fullAddress = place.name && place.formatted_address && !place.formatted_address.includes(place.name)
                ? `${place.name}, ${place.formatted_address}`
                : place.formatted_address;
              details.formatted_address = fullAddress;
              details.searchText = inputRef.current?.value || "";
              details.lat = lat;
              details.lng = lng;
              details.place_id = place.place_id;
              details.place_name = place.name;

              setSelectedLocation({ lat, lng });
              setAddressDetails(details);
              setSearchValue(fullAddress || "");
              setError(null);

              onLocationSelect?.(details);
            });
          } catch (err) {
            console.warn("Autocomplete init failed, using fallback search:", err);
          }
        }

        // Fallback search function using Geocoder
        const searchAddress = (searchText) => {
          if (!searchText || searchText.length < 3) return;
          
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { 
              address: searchText,
              componentRestrictions: { country: "TR" },
              region: "TR"
            },
            (results, status) => {
              if (status === "OK" && results && results.length > 0) {
                const place = results[0];
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();

                map.setCenter({ lat, lng });
                map.setZoom(17);
                marker.setPosition({ lat, lng });
                marker.setVisible(true);

                const details = parseAddressComponents(place, lat, lng);
                details.formatted_address = place.formatted_address;
                details.searchText = searchText;
                details.lat = lat;
                details.lng = lng;
                details.place_id = place.place_id;

                setSelectedLocation({ lat, lng });
                setAddressDetails(details);
                setSearchValue(place.formatted_address || "");
                setError(null);

                onLocationSelect?.(details);
              } else {
                setError(t("common:noLocationFound"));
              }
            }
          );
        };

        // Store search function reference for use in input
        searchFunctionRef.current = searchAddress;

        if (isMounted) {
          setIsLoading(false);
          setError(null);
        }
      } catch (err) {
        console.error("Map init error:", err);
        if (isMounted) {
          setError(t("common:mapNotInitialized"));
          setIsLoading(false);
        }
      }
    };

    // Load Google Maps script
    if (window.google?.maps) {
      initMap();
    } else {
      if (!document.getElementById("google-maps-script")) {
        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,marker&language=tr&region=TR`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (isMounted) {
            // Check if Google Maps loaded successfully
            if (window.google?.maps) {
              initMap();
            } else {
              console.error("Google Maps script loaded but google.maps is not available");
              setError(t("common:googleMapsNotInit"));
              setIsLoading(false);
            }
          }
        };
        script.onerror = (e) => {
          console.error("Google Maps script failed to load:", e);
          console.error("Current referrer:", document.referrer);
          console.error("Current origin:", window.location.origin);
          if (isMounted) {
            setError(`Google Maps yüklenemedi. Lütfen API anahtarı kısıtlamalarına "${window.location.origin}/*" ekleyin.`);
            setIsLoading(false);
          }
        };
        document.head.appendChild(script);
      } else {
        // Script loading, wait for it
        const checkInterval = setInterval(() => {
          if (window.google?.maps) {
            clearInterval(checkInterval);
            if (isMounted) initMap();
          }
        }, 100);
        
        return () => clearInterval(checkInterval);
      }
    }

    return () => {
      isMounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError(t("common:browserNoLocation"));
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(17);
        }
        
        placeMarker(lat, lng);
        setIsLoading(false);
      },
      (err) => {
        setError(t("common:locationError") + ": " + err.message);
        setIsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearSelection = () => {
    setSearchValue("");
    setSelectedLocation(null);
    setAddressDetails(null);
    markerRef.current?.map && (markerRef.current.map = null);
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  // Handle search
  const handleSearch = () => {
    const searchText = inputRef.current?.value?.trim();
    if (searchText && searchText.length >= 3 && searchFunctionRef.current) {
      setIsSearching(true);
      searchFunctionRef.current(searchText);
      setTimeout(() => setIsSearching(false), 1000);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Box */}
      <Card className="p-4 border-2 border-dashed border-slate-200 bg-slate-50/50">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <MapPin className="w-4 h-4" />
            Adres Ara (Google Maps)
          </div>
          
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                defaultValue={searchValue}
                placeholder={isLoading ? t("common:googleMapsLoading") : t("common:searchAddressPlaceholder")}
                disabled={isLoading}
                onKeyDown={handleKeyDown}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full h-12 pl-10 pr-10 text-base rounded-xl border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                data-testid="google-address-search"
              />
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              {searchValue && (
                <button
                  onClick={clearSelection}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 rounded-full"
                  type="button"
                >
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              )}
            </div>
            <Button
              type="button"
              onClick={handleSearch}
              disabled={isLoading || isSearching || !searchValue || searchValue.length < 3}
              className="h-12 px-4 rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common:search")}
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={getCurrentLocation}
              disabled={isLoading}
              className="rounded-lg"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Konumumu Bul
            </Button>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}
        </div>
      </Card>

      {/* Selected Address Display */}
      {addressDetails && (
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-green-800 text-sm">{t("common:secilenAdres")}</p>
              <p className="text-green-700 text-sm mt-1">{addressDetails.formatted_address}</p>
              
              <div className="flex flex-wrap gap-2 mt-2">
                {addressDetails.neighborhood && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Mahalle: {addressDetails.neighborhood}
                  </Badge>
                )}
                {addressDetails.street && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Cadde: {addressDetails.street}
                  </Badge>
                )}
                {addressDetails.building_no && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    No: {addressDetails.building_no}
                  </Badge>
                )}
                {addressDetails.district && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    İlçe: {addressDetails.district}
                  </Badge>
                )}
              </div>
              
              <p className="text-xs text-green-600 mt-2">
                📍 Koordinat: {selectedLocation?.lat.toFixed(6)}, {selectedLocation?.lng.toFixed(6)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Map Container */}
      <div className="relative rounded-xl overflow-hidden border-2 border-slate-200 shadow-sm">
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p className="text-sm text-slate-600">Harita yükleniyor...</p>
            </div>
          </div>
        )}
        
        <div
          ref={mapRef}
          style={{ height, width: "100%" }}
          className="bg-slate-100"
        />
        
        {/* Map Instructions */}
        <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-slate-600 shadow-sm inline-block">
            💡 Haritaya tıklayarak veya pin&apos;i sürükleyerek konum seçebilirsiniz
          </div>
        </div>
      </div>
    </div>
  );
}

// Export reverse geocode utility
export const reverseGeocode = async (lat, lng) => {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps) {
      reject(new Error("Google Maps not loaded"));
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];
        const components = parseAddressComponents(place, lat, lng);

        resolve({
          formatted_address: place.formatted_address,
          place_id: place.place_id,
          lat,
          lng,
          ...components,
        });
      } else {
        reject(new Error("Geocoding failed: " + status));
      }
    });
  });
};
